import React, { useMemo, useState } from 'react'
import {
  Box, Button, Card, CardContent, Chip, Divider, Grid, Typography, makeStyles
} from '@material-ui/core'
import ChatIcon from '@material-ui/icons/Chat'
import EventNoteIcon from '@material-ui/icons/EventNote'
import VisibilityIcon from '@material-ui/icons/Visibility'
import AssignmentIcon from '@material-ui/icons/Assignment'
import FolderIcon from '@material-ui/icons/Folder'
import { Link as RouterLink } from 'react-router-dom'
import { gregorianToJalali, thousandsSeparate, toFarsiDigits } from '../../assets/utils'
import { groupByContractor } from './boardUtils'
import ContractMessagesDialog from './ContractMessagesDialog'
import ContractOpensDialog from './ContractOpensDialog'
import { trackContractOpen } from '../../api/contractOpens'
import { isAdminImpersonatingCached } from '../../api/impersonation'

/** مرتب‌سازیِ نزولیِ تاریخ (جدیدترین اول) */
const byDateDesc = (a, b) => {
  const da = new Date(a?.date || 0).getTime()
  const db = new Date(b?.date || 0).getTime()
  if (da !== db) return db - da
  return (b?.id || 0) - (a?.id || 0)
}

const useStyles = makeStyles(() => ({
  groupBox: { marginBottom: 20 },
  groupHead: {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
    padding: '8px 12px', marginBottom: 10,
    background: 'linear-gradient(90deg, #eef1ff 0%, #ffffff 100%)',
    border: '1px solid #e3e6f2', borderRight: '4px solid #1a237e',
    borderRadius: 10,
  },
  groupName: { fontWeight: 800, fontSize: 15, color: '#1a237e' },
  countBadge: {
    fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: '3px 10px',
    background: '#e8eaf6', color: '#283593', whiteSpace: 'nowrap',
  },
  goldBadge: {
    fontSize: 11.5, fontWeight: 800, borderRadius: 999, padding: '3px 10px',
    color: '#8a6100', background: 'linear-gradient(135deg, #fff3c4 0%, #ffe082 100%)',
    border: '1px solid #e6b800', whiteSpace: 'nowrap',
  },
  grayBadge: {
    fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: '3px 10px',
    background: '#f1f2f8', color: '#7a80a3', whiteSpace: 'nowrap',
  },
  card: {
    borderRadius: 14,
    border: '1px solid #e3e6f2',
    boxShadow: '0 2px 10px rgba(20,30,80,.06)',
    transition: 'box-shadow .2s ease, transform .15s ease',
    height: '100%',
    display: 'flex', flexDirection: 'column',
  },
  cardClickable: {
    cursor: 'pointer',
    '&:hover': { boxShadow: '0 8px 22px rgba(20,30,80,.16)', transform: 'translateY(-2px)' },
  },
  cardHeader: {
    background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
    color: '#fff', padding: '10px 16px',
    borderTopLeftRadius: 14, borderTopRightRadius: 14,
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
  },
  contractNo: { fontSize: 21, fontWeight: 800, color: '#fff', letterSpacing: '.5px' },
  contractDate: { fontSize: 13.5, color: 'rgba(255,255,255,.9)', fontWeight: 700 },
  amount: {
    marginTop: 10, padding: '8px 10px', borderRadius: 10,
    background: '#f7f8fc', border: '1px solid #e3e6f2',
  },
  amountTitle: { fontSize: 11.5, fontWeight: 800, color: '#1a237e' },
  amountValue: { fontSize: 15, fontWeight: 800, color: '#283593' },
  amountBreak: { fontSize: 11.5, color: '#5a6b8c' },
  subject: {
    fontSize: 13, color: '#43476a', lineHeight: 1.9,
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  chip: { fontSize: 11, fontWeight: 700 },
  badge: { fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' },
}))

const nameOf = (obj) => {
  if (!obj) return '—'
  if (typeof obj === 'number' || typeof obj === 'string') return toFarsiDigits(obj)
  return toFarsiDigits(obj.name ?? obj.SupervisorName ?? obj.full_name ?? 'نامشخص')
}

/**
 * کارتِ قراردادها — ظاهرِ پنلِ پیمانکار، با **اطلاعاتِ محدود**:
 * سازمان طرف قرارداد · شماره · تاریخ · موضوع · طرفِ مقابل · وضعیت اطلاعات
 * گروه‌بندی: هر پیمانکار یک سربرگ با تعدادِ قراردادهایش؛ مشترک‌ها بالاتر.
 * کلیک روی کارت = باز شدنِ صورت‌وضعیت (فقط برای پیمانکارِ مشترک).
 */
export default function ContractCards({
  rows = [],
  showConsultant = false,
  showSupervisor = false,
  onOpenRequests,
  sortMode = 'name',
  supervisorLinks = false,
}) {
  const classes = useStyles()
  // در حالتِ «فقط تاریخ نزولی» گروه‌بندی نمی‌شود
  const groups = useMemo(
    () => (sortMode === 'date'
      ? [{ key: '__all__', name: '', rows: [...(rows || [])].sort(byDateDesc), count: (rows || []).length, subscribed: false, flat: true }]
      : groupByContractor(rows, sortMode)),
    [rows, sortMode]
  )
  const [msgFor, setMsgFor] = useState(null)
  const [opensFor, setOpensFor] = useState(null)
  // «گزارش بازدید» فقط برای ادمینی که از پنلِ ادمین «ورود به عنوان» کرده است
  const canSeeOpens = isAdminImpersonatingCached()

  if (!groups.length) {
    return (
      <Typography style={{ textAlign: 'center', color: '#7a80a3', padding: '18px 0' }}>
        قراردادی برای نمایش نیست
      </Typography>
    )
  }

  return (
    <Box data-tour="contracts-list">
      {groups.map((g) => (
        <Box key={g.key} className={classes.groupBox}>
          {!g.flat && (
            <Box className={classes.groupHead}>
              <Typography className={classes.groupName}>🏢 {toFarsiDigits(g.name)}</Typography>
              <span className={classes.countBadge}>{toFarsiDigits(g.count)} قرارداد</span>
              {g.subscribed ? (
                <span className={classes.goldBadge}>★ مشترک</span>
              ) : (
                <span className={classes.grayBadge}>غیرمشترک — اطلاعات نمایش داده نمی‌شود</span>
              )}
            </Box>
          )}

          <Grid container spacing={2}>
            {g.rows.map((row) => {
              const clickable = !row.hidden_reason && !!onOpenRequests
              return (
                <Grid item xs={12} md={6} key={row.id}>
                  <Card
                    className={classes.card + (clickable ? ' ' + classes.cardClickable : '')}
                    elevation={0}
                    onClick={() => { if (clickable) { trackContractOpen(row.id); onOpenRequests(row) } }}
                    title={clickable ? 'برای دیدنِ صورت‌وضعیت کلیک کنید' : (row.hidden_reason || '')}
                  >
                    <Box className={classes.cardHeader}>
                      <span className={classes.contractNo} dir="ltr">{toFarsiDigits(row.number || '—')}</span>
                      <span className={classes.contractDate}>
                        {row.date ? gregorianToJalali(row.date) : ''}
                      </span>
                    </Box>
                    <CardContent style={{ padding: '12px 16px 14px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <Typography className={classes.subject}>
                        {toFarsiDigits(row.subject || 'نامشخص')}
                      </Typography>

                      <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 6, marginTop: 8 }}>
                        <Chip size="small" className={classes.chip}
                          label={`🏛 ${toFarsiDigits(row.contractee || '—')}`}
                          style={{ background: '#eef1ff', color: '#1a237e' }} />
                        {showConsultant && (
                          <Chip size="small" className={classes.chip}
                            label={`مشاور: ${nameOf(row.consultant)}`}
                            style={{ background: '#f4f6fb', color: '#3949ab' }} />
                        )}
                        {showSupervisor && (
                          <Chip size="small" className={classes.chip}
                            label={`ناظر: ${nameOf(row.supervisor)}`}
                            style={{ background: '#f4f6fb', color: '#6a1b9a' }} />
                        )}
                      </Box>

                      <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 6, marginTop: 8 }}>
                        {row.hidden_reason ? (
                          <span className={classes.badge} title={row.hidden_reason}
                            style={{ background: '#ffebee', color: '#b71c1c' }}>
                            پیمانکار مشترک نیست
                          </span>
                        ) : (
                          <>
                            <span className={classes.badge} style={{ background: '#e8f5e9', color: '#2e7d32' }}>
                              قابل مشاهده — برای صورت‌وضعیت کلیک کنید
                            </span>
                            <Box className={classes.amount} style={{ width: '100%' }}>
                              <Typography className={classes.amountTitle}>مبلغ قرارداد</Typography>
                              <Typography className={classes.amountValue}>
                                {toFarsiDigits(thousandsSeparate(row.contract_total || 0))}
                              </Typography>
                              <Typography className={classes.amountBreak}>
                                مبلغ اولیه: {toFarsiDigits(thousandsSeparate(row.contract_cost || 0))}
                                {'  ·  '}
                                افزایش/کاهش: {toFarsiDigits(thousandsSeparate(row.increase_decrease_cost || 0))}
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Box>

                      <Divider style={{ margin: '12px 0 10px', borderColor: '#eef0f8' }} />

                      <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                        {supervisorLinks && !row.hidden_reason && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              component={RouterLink}
                              to={`/supervisor/contracts/${row.id}/requests`}
                              startIcon={<AssignmentIcon style={{ fontSize: 15 }} />}
                              style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'none', color: '#00695c', borderColor: '#00695c' }}
                            >
                              درخواست‌های صورت‌وضعیت
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              component={RouterLink}
                              to={`/supervisor/contracts/${row.id}/documents`}
                              startIcon={<FolderIcon style={{ fontSize: 15 }} />}
                              style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'none', color: '#4527a0', borderColor: '#4527a0' }}
                            >
                              اسناد قرارداد
                            </Button>
                          </>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          component={RouterLink}
                          to={`/contracts/${row.id}/delay`}
                          startIcon={<EventNoteIcon style={{ fontSize: 15 }} />}
                          style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'none', color: '#b71c1c', borderColor: '#b71c1c' }}
                        >
                          توقف و تأخیر
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          component={RouterLink}
                          to={`/contracts/${row.id}/prepayment`}
                          style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'none', color: '#6a1b9a', borderColor: '#6a1b9a' }}
                        >
                          پیش‌پرداخت
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ChatIcon style={{ fontSize: 15 }} />}
                          onClick={(e) => { e.stopPropagation(); setMsgFor(row) }}
                          style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'none' }}
                        >
                          گزارش پیام‌ها
                        </Button>
                        {canSeeOpens && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon style={{ fontSize: 15 }} />}
                            onClick={(e) => { e.stopPropagation(); setOpensFor(row) }}
                            style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'none' }}
                          >
                            گزارش بازدید
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </Box>
      ))}
      <ContractMessagesDialog
        open={!!msgFor}
        contractId={msgFor?.id}
        contractNumber={msgFor?.number}
        onClose={() => setMsgFor(null)}
      />
      <ContractOpensDialog
        open={!!opensFor}
        contractId={opensFor?.id}
        contractNumber={opensFor?.number}
        onClose={() => setOpensFor(null)}
      />
    </Box>
  )
}
